import { FormControl, FormGroup } from '@angular/forms';
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef as MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/dialog';

import {
  IEmailForm,
  IEmailTemplateInfo,
  ITemplatePayload
} from './../../../../models/email';
import { DialogEmailTypeEnum } from './../../../../models/email';
import { EmailService } from 'src/app/services/api/email.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { DataService } from 'src/app/services/data.service';
import { HandlebarsService } from 'src/app/services/handlebars.service';
import { TemplateVariableService } from 'src/app/services/template-variable.service';
import {
  ILockedTemplateBody,
  TemplateBodyLockService
} from 'src/app/services/template-body-lock.service';
import { ApiDialogService } from 'src/app/services/api/api-cdialog.service';
import { DialogTypeEnum, IDialogConfirm } from 'src/app/models/dialog';

@Component({
  selector: 'app-modal-email',
  templateUrl: './modal-email.component.html',
  styleUrls: ['./modal-email.component.scss']
})
export class ModalEmailComponent implements OnInit {
  form!: FormGroup<IEmailForm>;

  IDialogEmailTypeEnum = DialogEmailTypeEnum;

  templateVariables: { id: string; text: string }[] = [];

  DialogEmailTypeEnum = DialogEmailTypeEnum;

  isBankSelectInputDisabled = false;

  /** Valores crus vindos da API, base do diff de variáveis ao salvar. */
  originalSubject = '';
  originalTemplate = '';

  /**
   * Corpo recortado para o editor: os trechos fixos (tabela, `{{#if}}`...)
   * ficam guardados aqui e voltam intactos no save.
   */
  lockedBody: ILockedTemplateBody = {
    prefix: '',
    suffix: '',
    editable: '',
    blocks: [],
    labels: [],
    previews: []
  };

  /**
   * Função, não array: o editor desenha o trecho no `patchValue`, antes de o
   * Angular atualizar um `@Input`, e precisa ler o `lockedBody` já recortado.
   */
  blockPreview = (blockId: number): string | null =>
    this.lockedBody.previews[blockId] ?? null;

  banks = [
    { name: 'Santander', id: 'Santander' },
    { name: 'Itaú', id: 'Itau' },
    { name: 'Bradesco', id: 'Bradesco' }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { type: string; infos: IEmailTemplateInfo },
    private _emailService: EmailService,
    private _snackbarService: SnackbarService,
    public dialogRef: MatDialogRef<ModalEmailComponent>,
    private _dataService: DataService,
    private _handlebarsService: HandlebarsService,
    private _templateVariableService: TemplateVariableService,
    private _templateBodyLockService: TemplateBodyLockService,
    private _dialogService: ApiDialogService
  ) {}

  ngOnInit(): void {
    this.getTemplateById();
    this.buildFormGroup();
    this.checkTemplateType();
    this.getTemplateVariables();
  }

  checkTemplateType(): void {
    if (this.data) {
      this.data.type = this.data?.type ?? DialogEmailTypeEnum.Invoice;
    }
  }

  getTemplateById(): void {
    if (this.data?.infos?.TemplateId) {
      this._emailService.getTemplateById(this.data.infos.TemplateId).subscribe({
        next: (res) => {
          // Guardado cru para o diff de variáveis em `saveConfig`.
          this.originalSubject = res?.Data?.Subject ?? '';
          this.originalTemplate = res?.Data?.Template ?? '';
          this.lockedBody = this._templateBodyLockService.split(
            this.originalTemplate
          );

          // As variáveis chegam como Handlebars cru e são mascaradas para
          // exibição; `saveConfig` desfaz a máscara antes de enviar à API.
          // O corpo vai ao editor já sem os trechos fixos.
          const dataToPatch = {
            bank: res?.Data?.EventName?.split('.')[0],
            ...res?.Data,
            Subject: this._templateVariableService.maskHtml(res?.Data?.Subject),
            Template: this._templateVariableService.maskHtml(
              this.lockedBody.editable
            )
          };

          this.form.patchValue(dataToPatch);

          this.isBankSelectInputDisabled = true;
          this.form.controls['bank'].disable();
        }
      });
    }
  }

  buildFormGroup(): void {
    this.form = new FormGroup<IEmailForm>({
      bank: new FormControl(
        { value: '', disabled: this.isBankSelectInputDisabled },
        { nonNullable: true }
      ),
      CompanyId: new FormControl(''),
      EventName: new FormControl('', { nonNullable: true }),
      EventType: new FormControl('', { nonNullable: true }),
      Subject: new FormControl('', { nonNullable: true }),
      Template: new FormControl('', { nonNullable: true }),
      TemplateId: new FormControl('', { nonNullable: true })
    });
  }

  getTemplateVariables(): void {
    const template = this.data.infos;

    const insertVariablesToTemplateVariablesArray = (
      payload: ITemplatePayload,
      prefix = ''
    ) => {
      if (
        payload.IsList &&
        payload.ListProperties &&
        payload.ListProperties.length > 0
      ) {
        const listLabel = this._templateVariableService.label(
          `${prefix && 'this.'}${payload.Name}`
        );

        const item = {
          id: `{{#each ${prefix && 'this.'}${payload.Name}}} {{/each}}}}`,
          text: `Lista: ${listLabel}`
        };
        const itemTable = {
          id: `{{#eachTable ${prefix && 'this.'}${payload.Name}}}`,
          text: `Tabela: ${listLabel}`
        };
        this.templateVariables.push(...[item, itemTable]);

        payload.ListProperties.forEach((listProperty) => {
          insertVariablesToTemplateVariablesArray(listProperty, payload.Name);
        });

        return;
      }

      const expression = `${prefix && 'this.'}${payload.Name}`;

      const item = {
        id: `{{${expression}}}`,
        text: this._templateVariableService.label(expression)
      };
      this.templateVariables.push(item);
    };

    this._emailService
      .getTemplateVariables({
        eventType: template?.EventType ?? 'Invoice',
        eventName: template?.EventName
      })
      .subscribe((res) => {
        res?.forEach((payload) =>
          insertVariablesToTemplateVariablesArray(payload)
        );

        this.templateVariables.sort((a, b) => a.text.localeCompare(b.text));
      });
  }

  saveConfig(): void {
    const template = this.form.getRawValue();

    // Desfaz a máscara de apresentação (o que vai para a API é sempre o
    // Handlebars cru), mas SOMENTE no campo que o usuário realmente editou.
    //
    // O que o CKEditor devolve não é o que entrou: os templates gravados usam
    // estilo inline, imagem e `{{#each}}` dentro da tabela, e o editor descarta
    // o que não existe no schema dele. Medido em 2026-09-16 com o template real
    // de `Parceiro.Transfer`: o corpo perde ~55% do conteúdo (estilos,
    // assinatura, `{{EmailLink}}`) e os `{{#each}}` são arrancados de dentro da
    // tabela — o e-mail sai com a tabela vazia, sem nenhum contrato.
    //
    // Reenviar o valor original quando o campo não foi tocado garante que abrir
    // o modal para mexer só no Assunto não regrave o corpo, e vice-versa.
    template.Subject = this.form.get('Subject')?.dirty
      ? this._templateVariableService.unmaskSubject(template.Subject)
      : this.originalSubject;

    // O editor só viu o corpo sem os trechos fixos (ver TemplateBodyLockService);
    // `merge` os devolve intactos e desfaz a máscara das variáveis.
    const changedBlocks = this.form.get('Template')?.dirty
      ? this._templateBodyLockService.changedBlocks(
          template.Template,
          this.lockedBody
        )
      : [];

    template.Template = this.form.get('Template')?.dirty
      ? this._templateBodyLockService.merge(template.Template, this.lockedBody)
      : this.originalTemplate;

    const missing = this.variableDiff(
      [this.originalSubject, template.Subject],
      [this.originalTemplate, template.Template]
    );

    // Na edição não se insere variável nova. O menu `{` está desligado, mas
    // copiar e colar um chip existente ainda duplicaria a variável.
    const added = this.data?.infos?.TemplateId
      ? this.variableDiff(
          [template.Subject, this.originalSubject],
          [template.Template, this.originalTemplate]
        )
      : [];

    if (missing.length > 0 || added.length > 0 || changedBlocks.length > 0) {
      this.showBlockedSave(missing, added, changedBlocks);
      return;
    }

    this.persistTemplate(template);
  }

  /**
   * Rótulos das variáveis que estão em `from` e faltam em `to`, somando assunto
   * e corpo. Cada par é `[from, to]`.
   */
  variableDiff(...pairs: [string, string][]): string[] {
    return (
      pairs
        .flatMap(([from, to]) =>
          this._templateVariableService.missingVariables(from, to)
        )
        // Blocos (`{{#if}}`, `{{/each}}`...) já são cobertos por `changedBlocks`.
        .filter(({ mention }) => !/^\{\{~?\s*(?:[#/^!>]|else\b)/.test(mention))
        .map(({ label, count }) => (count > 1 ? `${label} (${count}x)` : label))
    );
  }

  /**
   * Variáveis e trechos fixos não podem sair do template: sem eles o e-mail sai
   * com valor em branco ou, no caso de um `{{#if}}` sem par, nem é enviado.
   * Variável nova também não pode entrar na edição. O save é bloqueado; Ctrl+Z
   * no editor desfaz a mudança.
   */
  showBlockedSave(
    missingVariables: string[],
    addedVariables: string[],
    changedBlocks: string[]
  ): void {
    const parts = [
      missingVariables.length > 0
        ? `variáveis removidas: ${missingVariables.join(', ')}`
        : '',
      addedVariables.length > 0
        ? `variáveis adicionadas: ${addedVariables.join(', ')}`
        : '',
      changedBlocks.length > 0
        ? `trechos fixos removidos ou duplicados: ${changedBlocks.join(', ')}`
        : ''
    ].filter(Boolean);

    const dialogData: IDialogConfirm = {
      type: DialogTypeEnum.Confirmation,
      template: {
        boldTitle: '',
        title: `Não é possível salvar — ${parts.join('; ')}. Use Ctrl+Z no editor para desfazer.`,
        confirm: 'Voltar e corrigir'
      }
    };

    this._dialogService.openDialog(dialogData);
  }

  persistTemplate(template: IEmailTemplateInfo): void {
    if (this.form.get('Template')?.dirty) {
      template.Template = this.handleTemplate(template.Template);
    }

    if (this.data?.infos?.TemplateId) {
      this.updateTemplate(template);
    } else {
      this.createTemplate(template);
    }
  }

  handleTemplate(templateString: string): string {
    // Regex em vez de `startsWith('<html>')`: o `merge` devolve o cabeçalho
    // original, que pode ter espaço, doctype ou atributo antes/no `<html>`.
    if (!/<html[\s>]/i.test(templateString)) {
      templateString = `<html><body>${templateString}</body></html>`;
    }

    templateString = this._handlebarsService.insertHandlebars(templateString);
    return templateString;
  }

  createTemplate(template: IEmailTemplateInfo): void {
    template.EventType = this.data.type;
    template.EventName = `${template.bank}.${this.data.type}`;
    template.CompanyId = null;

    const { CompanyId, EventName, EventType, Subject, Template } = template;

    const templateInfo = {
      CompanyId,
      EventName,
      EventType,
      Subject,
      Template
    };

    this._emailService.createTemplate(templateInfo).subscribe({
      next: (res) => {
        if (res) {
          this._snackbarService.openSnackBar(
            'Template criado com sucesso!',
            'success'
          );

          this._dataService.triggerEmailModalClose();
          this.dialogRef.close();
        }
      }
    });
  }

  updateTemplate(template: IEmailTemplateInfo): void {
    this._emailService.updateTemplate(template).subscribe({
      next: (res) => {
        if (res) {
          this._snackbarService.openSnackBar(
            'Template atualizado com sucesso!',
            'success'
          );

          this._dataService.triggerEmailModalClose();
          this.dialogRef.close();
        }
      }
    });
  }
}
